<template>
  <div class="admin-page">
    <header class="page-head">
      <h1>Admin Panel</h1>
      <p class="sub">Manage users, invitations, and trigger logs.</p>
    </header>

    <p v-if="statusMessage" :class="['toast', statusType]">{{ statusMessage }}</p>
<section class="card">
  <header class="card__head">
    <h2>Result Upload Control</h2>
    <p class="sub">
      Temporarily pause or resume result uploads if you detect an issue.
    </p>
  </header>

  <div class="form-grid">
    <div class="field">
      <span>Current Status</span>
      <p>
        <span
          class="pill"
          :class="uploadPaused ? 'pill--danger' : 'pill--success'"
        >
          {{ uploadPaused ? 'Uploads Paused' : 'Uploads Active' }}
        </span>
      </p>
      <small class="muted">
        {{
          uploadPaused
            ? 'New results should be held back until you resume uploads.'
            : 'Results will be uploaded as workers finish processing.'
        }}
      </small>
    </div>

    <div class="actions">
      <button
        type="button"
        class="btn"
        :class="uploadPaused ? 'btn--secondary' : 'btn--danger'"
        @click="toggleUploadPause"
        :disabled="isTogglingUploadPause"
      >
        <span v-if="isTogglingUploadPause">Working…</span>
        <span v-else>
          {{ uploadPaused ? 'Resume uploads' : 'Pause uploads' }}
        </span>
      </button>
    </div>
  </div>
</section>
<section class="card">
  <header class="card__head">
    <h2>Analysis Tool Pricing</h2>
    <p class="sub">
      Configure how many credits are consumed per analysis request, and (optionally) a temporary discount.
    </p>
  </header>

<div class="form-grid">
  <!-- Base price -->
  <div class="field field--full">
    <label for="creditsPerAnalysis">Credits per Analysis (baseline)</label>
    <input
      id="creditsPerAnalysis"
      type="number"
      min="1"
      v-model.number="creditsPerAnalysis"
    />
    <small class="muted">
      Baseline credit cost. Per-mode values below override this for each mode.
    </small>
  </div>

  <!-- Per-mode pricing -->
  <div class="field field--full">
    <label>Per-mode Credit Costs</label>

    <div class="per-mode-grid">
      <!-- Normal mode (mode = bucket) -->
      <div class="field">
        <label for="creditsPerNormalMode">Normal Mode</label>
        <input
          id="creditsPerNormalMode"
          type="number"
          min="1"
          v-model.number="creditsPerNormalMode"
        />
        <small class="muted">
          Cost when users run a request in <strong>Normal</strong> mode.
        </small>
        <p class="muted tiny" v-if="effectiveCostNormalMode !== null">
          Effective now:
          <strong>{{ effectiveCostNormalMode }} credits</strong>
        </p>
      </div>

      <!-- Advanced mode (mode = normal) -->
      <div class="field">
        <label for="creditsPerAdvancedMode">Advanced Mode</label>
        <input
          id="creditsPerAdvancedMode"
          type="number"
          min="1"
          v-model.number="creditsPerAdvancedMode"
        />
        <small class="muted">
          Cost when users run a request in <strong>Advanced</strong> mode.
        </small>
        <p class="muted tiny" v-if="effectiveCostAdvancedMode !== null">
          Effective now:
          <strong>{{ effectiveCostAdvancedMode }} credits</strong>
        </p>
      </div>

      <!-- LinkCheck mode (mode = linkcheck) -->
      <div class="field">
        <label for="creditsPerLinkcheck">LinkCheck Tool</label>
        <input
          id="creditsPerLinkcheck"
          type="number"
          min="1"
          v-model.number="creditsPerLinkcheck"
        />
        <small class="muted">
          Credits consumed when running the <strong>LinkCheck</strong> tool.
        </small>
        <p class="muted tiny" v-if="effectiveCostLinkcheck !== null">
          Effective now:
          <strong>{{ effectiveCostLinkcheck }} credits</strong>
        </p>
      </div>
    </div>
  </div>

  <!-- Effective cost summary (baseline) -->
  <div class="field field--full">
    <span>Current Effective Cost (baseline)</span>

    <p class="muted" v-if="creditsPerAnalysis">
      <strong v-if="effectiveAnalysisCost !== null">
        {{ effectiveAnalysisCost }} credits
      </strong>

      <template
        v-if="discountActive && discountAmount > 0 && creditsPerAnalysis"
      >
        <span v-if="discountAppliesNow">
          &nbsp;(base {{ creditsPerAnalysis }} − discount {{ discountAmount }})
        </span>
        <span v-else>
          &nbsp;(base {{ creditsPerAnalysis }} — discount configured but not currently active)
        </span>
      </template>
    </p>

    <p class="muted" v-else>
      Set a base credit cost to see the effective value.
    </p>

    <p
      v-if="discountStatusText"
      class="muted tiny"
      style="margin-top: 4px;"
    >
      {{ discountStatusText }}
    </p>
  </div>



    <!-- Discount panel -->
    <div class="field field--full">
      <div class="pricing-discount-panel">
        <div class="pricing-discount-panel__head">
          <label class="checkbox-inline">
            <input type="checkbox" v-model="discountActive" />
            Enable Temporary Discount
          </label>
          <small class="muted">
            When enabled, this discount will apply to analysis requests
            while it is active and within its configured time window.
          </small>
        </div>

        <div class="pricing-discount-panel__grid">
          <div class="field">
            <label for="discountAmount">Discount Amount</label>
            <input
              id="discountAmount"
              type="number"
              min="0"
              :max="creditsPerAnalysis || 999999"
              v-model.number="discountAmount"
              :disabled="!discountActive"
            />
            <small class="muted">
              Number of credits to subtract while the discount is active.
            </small>
          </div>

          <div class="field">
            <label for="discountStartsAt">Discount Starts At (UTC)</label>
            <input
              id="discountStartsAt"
              ref="discountStartsAtInput"
              type="text"
              placeholder="YYYY-MM-DD HH:MM:SS"
              v-model="discountStartsAt"
              :disabled="!discountActive"
            />
            <small class="muted">
              Optional. Leave blank to start immediately. Format: 2025-11-20 09:00:00
            </small>
          </div>

          <div class="field">
            <label for="discountEndsAt">Discount Ends At (UTC)</label>
            <input
              id="discountEndsAt"
              ref="discountEndsAtInput"
              type="text"
              placeholder="YYYY-MM-DD HH:MM:SS"
              v-model="discountEndsAt"
              :disabled="!discountActive"
            />
            <small class="muted">
              Optional. Leave blank for no expiry. Format: 2025-12-31 23:59:59
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Last updated -->
    <div class="field field--full">
      <span>Last Updated</span>
      <p class="muted" v-if="pricingUpdatedAt">
        {{ pricingUpdatedAt }} by {{ pricingUpdatedBy || 'unknown' }}
        ({{ pricingUpdatedIp || 'IP unknown' }})
      </p>
      <p class="muted" v-else>Not yet updated.</p>
    </div>
  </div>

  <div class="actions">
    <button
      class="btn"
      :disabled="pricingLoading || pricingSaving"
      @click="saveAnalysisPricing"
    >
      <span v-if="pricingSaving">Saving...</span>
      <span v-else>Save Pricing Settings</span>
    </button>
  </div>
</section>


    <!-- Create User -->
    <section class="card">
      <header class="card__head">
        <h2>Create User</h2>
      </header>
      <form class="form-grid" @submit.prevent="createUser">
        <label class="field">
          <span>Username</span>
          <input v-model="username" type="text" required data-1p-ignore maxlength="50" placeholder="e.g. analyst_jane" />
        </label>
        <label class="field">
          <span>Password</span>
          <input v-model="password" type="password" required maxlength="50" placeholder="••••••••" />
        </label>
        <div class="actions">
          <button type="submit" class="btn">Create User</button>
        </div>
      </form>
    </section>

    <!-- Invite User by Email -->
    <section class="card">
      <header class="card__head">
        <h2>Invite User</h2>
        <p class="muted">Send an invite that links a username + email.</p>
      </header>
      <form class="form-grid" @submit.prevent="sendInvite">
        <label class="field">
          <span>Username</span>
          <input v-model="inviteUsername" type="text" required data-1p-ignore maxlength="50" placeholder="e.g. new_investigator" />
        </label>
        <label class="field">
          <span>Email</span>
          <input v-model="email" type="email" required data-1p-ignore maxlength="100" placeholder="name@company.com" />
        </label>
        <div class="actions">
          <button type="submit" class="btn">Send Invite</button>
        </div>
      </form>

      <div class="divider"></div>

      <header class="card__head -tight">
        <h3>Simple Invite</h3>
        <p class="muted">Just send an email invite (no username pre-set).</p>
      </header>
      <form class="form-inline" @submit.prevent="sendSimpleInvite">
        <label class="field grow">
          <span class="sr-only">Email</span>
          <input v-model="simpleInviteEmail" type="email" data-1p-ignore required maxlength="100" placeholder="name@company.com" />
        </label>
        <button type="submit" class="btn">Send Invite</button>
      </form>
    </section>

    <!-- Pending Invites -->
    <section class="card">
      <header class="card__head">
        <h2>Pending Invites</h2>
        <p class="muted">Resend or revoke outstanding invitations.</p>
      </header>

      <div v-if="pendingInvites.length" class="table-wrap">
        <table class="nice-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Expires</th>
              <th>Actions</th>

            </tr>
          </thead>
          <tbody>
            <tr v-for="invite in pendingInvites" :key="invite.id">
              <td class="semi">{{ invite.username }}</td>
              <td class="mono truncate">
                <span :title="invite.email">{{ invite.email }}</span>
                <button class="icon-btn" @click="copy(invite.email)" title="Copy email">📋</button>
              </td>
              <td class="semi">{{ invite.expires_at }}</td>
              <td>
                <div class="row-actions">
                  <button class="btn btn--ghost small" @click="resendInvite(invite)">Resend</button>
                  <button class="btn btn--danger small" @click="revokeInvite(invite.id)">Revoke</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="empty">No pending invites.</p>

      <!-- Mobile cards -->
      <div class="cards" v-if="pendingInvites.length">
        <article v-for="invite in pendingInvites" :key="'m-inv-'+invite.id" class="mcard">
          <header class="mcard__head">
            <div class="mcard__title semi">{{ invite.username }}</div>
          </header>
          <dl class="kv">
            <div><dt>Email</dt><dd class="mono truncate">
              <span :title="invite.email">{{ invite.email }}</span>
              <button class="icon-btn" @click="copy(invite.email)" title="Copy email">📋</button>
            </dd></div>
            <div><dt>Expires</dt><dd>{{ invite.expires_at }}</dd></div>
          </dl>
          <div class="mcard__actions">
            <button class="btn btn--ghost small" @click="resendInvite(invite)">Resend</button>
            <button class="btn btn--danger small" @click="revokeInvite(invite.id)">Revoke</button>
          </div>
        </article>
      </div>
    </section>

    <!-- Existing Users -->
    <section class="card">
      <header class="card__head">
        <div>
          <h2>Existing Users</h2>
          <p class="sub">
            Admins and moderators receive Case Buddy through their role. Use the
            Case Buddy control below to grant or remove access for ordinary accounts.
          </p>
        </div>
      </header>

      <div v-if="users.length" class="table-wrap">
        <table class="nice-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Advanced Mode</th>
              <th>Case Buddy</th>
              <th>Actions</th>
              <th>Restricted</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td class="semi">{{ user.username }}</td>
              <td>
                <span class="pill" :class="rolePill(user.role)">{{ user.role }}</span>
              </td>
              <td>
                <span class="pill" :class="user.can_use_normal_mode ? 'pill--ok' : 'pill--queued'">
                  {{ user.can_use_normal_mode ? 'Enabled' : 'Disabled' }}
                </span>
              </td>
              <td>
                <span class="pill" :class="caseBuddyAccessPill(user)">
                  {{ caseBuddyAccessLabel(user) }}
                </span>
                <small
                  v-if="caseBuddyRestrictionActive(user) && caseBuddyHasAccessSource(user)"
                  class="muted permission-note"
                >
                  Account restriction overrides this access.
                </small>
              </td>
              <td>
                <div class="row-actions">
                  <button
                    v-if="user.role !== 'admin'"
                    class="btn btn--ghost small"
                    @click="changeRole(user.id)"
                  >
                    Change Role
                  </button>
                  <button
                    v-if="user.role !== 'admin'"
                    class="btn btn--ghost small"
                    @click="toggleNormalMode(user.id)"
                  >
                    {{ user.can_use_normal_mode ? 'Disable' : 'Enable' }} Advanced Mode
                  </button>
                  <button
                    v-if="canManageCaseBuddyPermission(user)"
                    class="btn btn--ghost small"
                    :disabled="caseBuddyPermissionButtonDisabled(user)"
                    @click="setCaseBuddyAccess(user, !user.can_use_case_buddy)"
                  >
                    <span v-if="isCaseBuddyPermissionSaving(user.id)">Saving…</span>
                    <span v-else>
                      {{ user.can_use_case_buddy ? 'Disable' : 'Enable' }} Case Buddy
                    </span>
                  </button>
                  <button
                    v-if="user.role !== 'admin'"
                    class="btn btn--danger small"
                    @click="confirmDeleteUser(user.id, user.username)"
                  >
                    Delete
                  </button>
                  <button
  v-if="user.role !== 'admin'"
  class="btn btn--ghost small"
  @click="setRestriction(user.id, !user.is_restricted)"
>
  {{ user.is_restricted ? 'Unrestrict' : 'Restrict' }}
</button>

                </div>
              </td>
              <td>
  <span class="pill" :class="user.is_restricted ? 'pill--danger' : 'pill--ok'">
    {{ user.is_restricted ? 'Restricted' : 'Active' }}
  </span>
</td>

            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="empty">No users found.</p>
<div v-if="total > 0" class="pagination-row">
  <div class="muted">
    Showing {{ showingFrom }}–{{ showingTo }} of {{ total }}
  </div>
  <div class="pager">
    <label>
      Rows:
      <select :value="limit" @change="changeLimit($event.target.value)">
        <option>10</option>
        <option>25</option>
        <option>50</option>
        <option>100</option>
      </select>
    </label>
    <button class="btn btn--ghost" :disabled="page===1" @click="prevPageUsers">Prev</button>
    <span>Page {{ page }} / {{ totalPages }}</span>
    <button class="btn btn--ghost" :disabled="page>=totalPages" @click="nextPageUsers">Next</button>
  </div>
</div>

      <!-- Mobile cards -->
      <div class="cards" v-if="users.length">
        <article v-for="user in users" :key="'m-user-'+user.id" class="mcard">
          <header class="mcard__head">
            <div class="mcard__title semi">{{ user.username }}</div>
            <span class="pill" :class="rolePill(user.role)">{{ user.role }}</span>
          </header>
          <dl class="kv">
            <div><dt>Advanced Mode</dt>
              <dd>
                <span class="pill" :class="user.can_use_normal_mode ? 'pill--ok' : 'pill--queued'">
                  {{ user.can_use_normal_mode ? 'Enabled' : 'Disabled' }}
                </span>
              </dd>
            </div>
            <div><dt>Case Buddy</dt>
              <dd>
                <span class="pill" :class="caseBuddyAccessPill(user)">
                  {{ caseBuddyAccessLabel(user) }}
                </span>
                <small
                  v-if="caseBuddyRestrictionActive(user) && caseBuddyHasAccessSource(user)"
                  class="muted permission-note"
                >
                  Account restriction overrides this access.
                </small>
              </dd>
            </div>
          </dl>
          <div class="mcard__actions">
            <button
              v-if="user.role !== 'admin'"
              class="btn btn--ghost small"
              @click="changeRole(user.id)"
            >Change Role</button>
            <button
              v-if="user.role !== 'admin'"
              class="btn btn--ghost small"
              @click="toggleNormalMode(user.id)"
            >{{ user.can_use_normal_mode ? 'Disable' : 'Enable' }} Advanced Mode</button>
            <button
              v-if="canManageCaseBuddyPermission(user)"
              class="btn btn--ghost small"
              :disabled="caseBuddyPermissionButtonDisabled(user)"
              @click="setCaseBuddyAccess(user, !user.can_use_case_buddy)"
            >
              <span v-if="isCaseBuddyPermissionSaving(user.id)">Saving…</span>
              <span v-else>{{ user.can_use_case_buddy ? 'Disable' : 'Enable' }} Case Buddy</span>
            </button>
            <button
              v-if="user.role !== 'admin'"
              class="btn btn--danger small"
              @click="confirmDeleteUser(user.id, user.username)"
            >Delete</button>
          </div>
        </article>
      </div>
    </section>

    <!-- To Address Trigger Logs -->
    <section class="card">
      <header class="card__head">
        <h2>To Address Trigger Logs</h2>
      </header>

      <div v-if="logs.length" class="table-wrap">
        <table class="nice-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Tx Hash</th>
              <th>Network</th>
              <th>Reason</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in logs" :key="log.id">
              <td class="mono">{{ log.id }}</td>
              <td class="mono truncate">
                <span :title="log.tx_hash">{{ log.tx_hash }}</span>
                <button class="icon-btn" @click="copy(log.tx_hash)" title="Copy tx hash">📋</button>
              </td>
              <td class="semi">{{ log.network }}</td>
              <td>{{ log.trigger_reason }}</td>
              <td class="semi">{{ log.timestamp }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="empty">No logs available.</p>

      <div class="pagination">
        <button class="btn btn--ghost" @click="prevPage" :disabled="logPage === 1">Prev</button>
        <span>Page {{ logPage }}</span>
        <button class="btn btn--ghost" @click="nextPage" :disabled="logPage * 20 >= totalLogs">Next</button>
      </div>
    </section>

    <!-- Quick Nav -->
    <nav class="quick-nav">
      <router-link to="/securitypanel">Dashboard</router-link>
      <router-link to="/requestmonitor">Request Monitor</router-link>
      <router-link to="/adminhqimages">HQ Images</router-link>
      <router-link to="/signupreview">Signup Review</router-link>
      <router-link to="/useraccounts">User Account Management</router-link>
      <router-link to="/tokenmetadata">Token Metadata</router-link>
      <router-link to="/supporttickets">Support Tickets</router-link>
      <router-link to="/securitytools">Tools</router-link>
      <router-link to="/PromoCodeManager">Promo Codes</router-link>
      <router-link to="/linkchecktool">LinkCheck</router-link>
    </nav>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirmation" class="modal" role="dialog" aria-modal="true" aria-labelledby="delTitle">
      <div class="modal__panel">
        <h3 id="delTitle">Delete user?</h3>
        <p>Are you sure you want to delete <strong>{{ userToDelete.username }}</strong>?</p>
        <div class="modal__actions">
          <button class="btn btn--danger" @click="deleteUser">Yes, delete</button>
          <button class="btn btn--ghost" @click="cancelDelete">Cancel</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
export default {
  name: 'AdminPage',
  data() {
    return {
      username: '',
      password: '',
      users: [],
      statusMessage: '',
      statusType: '',
      showDeleteConfirmation: false,
      userToDelete: { id: null, username: '' },
      inviteUsername: '',
      email: '',
      simpleInviteEmail: '',
      pendingInvites: [],
      logs: [],
      logPage: 1,
      totalLogs: 0,
      page: 1,
      limit: 25,
      total: 0,
      isLoading: false,
      caseBuddyPermissionSaving: {},
      // NEW
      uploadPaused: false,
      isTogglingUploadPause: false,
        creditsPerAnalysis: null,

         // NEW: per-mode pricing
    creditsPerNormalMode: null,
    creditsPerAdvancedMode: null,
    creditsPerLinkcheck: null,


    discountActive: false,
    discountAmount: 0,
      discountStartsAt: '',
    discountEndsAt: '',
    pricingUpdatedAt: null,
    pricingUpdatedBy: null,
    pricingUpdatedIp: null,
    pricingLoading: false,
    pricingSaving: false,
      // flatpickr instances
      discountStartPicker: null,
      discountEndPicker: null,
    };
  },  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit) || 1)
    },
    showingFrom() {
      return (this.page - 1) * this.limit + (this.users.length ? 1 : 0)
    },
    showingTo() {
      return (this.page - 1) * this.limit + this.users.length
    },
    
  // Effective costs per mode using the same discount logic
  effectiveCostNormalMode() {
    return this.computeEffectiveCostForBase(this.creditsPerNormalMode);
  },
  effectiveCostAdvancedMode() {
    return this.computeEffectiveCostForBase(this.creditsPerAdvancedMode);
  },
  effectiveCostLinkcheck() {
    return this.computeEffectiveCostForBase(this.creditsPerLinkcheck);
  },
  // NEW: does the discount apply *right now* according to the time window?
  discountAppliesNow() {
    if (!this.discountActive) return false;
    if (!this.creditsPerAnalysis || this.creditsPerAnalysis <= 0) return false;
    if (!this.discountAmount || this.discountAmount <= 0) return false;
    if (this.discountAmount > this.creditsPerAnalysis) return false;

    const now = new Date();
    // Convert local "now" to a UTC Date for comparison with stored UTC strings
    const nowUtcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    const nowUtc = new Date(nowUtcMs);

    let inWindow = true;

    if (this.discountStartsAt) {
      const start = this.parseUtcDateTime(this.discountStartsAt);
      if (!start || nowUtc < start) inWindow = false;
    }

    if (this.discountEndsAt) {
      const end = this.parseUtcDateTime(this.discountEndsAt);
      if (!end || nowUtc > end) inWindow = false;
    }

    return inWindow;
  },

  // NEW: what is the *current* effective cost if an analysis is run now?
   effectiveAnalysisCost() {
    return this.computeEffectiveCostForBase(this.creditsPerAnalysis);
  },
 
  // Optional: a short status label to show under the effective cost
  discountStatusText() {
    if (!this.discountActive || !this.discountAmount || !this.creditsPerAnalysis) {
      return '';
    }

    if (this.discountAppliesNow) {
      return 'Discount is currently active based on the configured UTC window.';
    }

    // If we have a future start time
    if (this.discountStartsAt) {
      const start = this.parseUtcDateTime(this.discountStartsAt);
      const now = new Date();
      const nowUtcMs = now.getTime() + now.getTimezoneOffset() * 60000;
      const nowUtc = new Date(nowUtcMs);

      if (start && nowUtc < start) {
        return 'Discount is configured but not yet active (starts at the given UTC time).';
      }
    }

    // Otherwise treat it as expired / out of window
    return 'Discount is configured but not currently active (outside its UTC time window).';
  }
  },  

  mounted() {
      this.initDatePickers();
  
    this.fetchUsers();
    this.fetchInvites();
    this.fetchLogs();
    this.fetchUploadPauseState();
  this.fetchAnalysisPricing();
  },
    beforeUnmount() {
    this.destroyDatePickers();
  },
  methods: {
   setRestriction(userId, restricted) {
  axios.post(
    'etherscan/set_user_restriction.php',
    { id: userId, restricted, reason: 'admin decision', until: '' },
    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
  )
  .then(res => {
    console.log('set_user_restriction success:', res.data);

    if (res.data && res.data.status === 'success') {
      this.statusMessage = restricted ? 'User restricted.' : 'User unrestricted.';
      this.statusType = 'success';
      this.fetchUsers();
    } else {
      this.statusMessage = (res.data && res.data.message) || 'Failed to update restriction.';
      this.statusType = 'error';
    }
  })
  .catch(err => {
    console.error('set_user_restriction error:', err);

    const serverMsg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      (typeof err?.response?.data === 'string' ? err.response.data : '') ||
      err?.message ||
      'Failed to update restriction.';

    this.statusMessage = serverMsg;
    this.statusType = 'error';
  });
}

,
      computeEffectiveCostForBase(baseCredits) {
    if (!baseCredits || baseCredits <= 0) return null;

    // No discount -> cost is just base
    if (!this.discountAppliesNow || !this.discountAmount || this.discountAmount <= 0) {
      return baseCredits;
    }

    const discount = Math.min(this.discountAmount || 0, baseCredits);
    const cost = baseCredits - discount;
    return cost < 0 ? 0 : cost;
  },

     parseUtcDateTime(str) {
    if (!str || typeof str !== 'string') return null;
    const m = str.match(
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/
    );
    if (!m) return null;

    const year   = Number(m[1]);
    const month  = Number(m[2]); // 1–12
    const day    = Number(m[3]);
    const hour   = Number(m[4]);
    const minute = Number(m[5]);
    const second = Number(m[6]);

    if (
      !Number.isFinite(year)   || !Number.isFinite(month)  ||
      !Number.isFinite(day)    || !Number.isFinite(hour)   ||
      !Number.isFinite(minute) || !Number.isFinite(second)
    ) {
      return null;
    }

    // Construct as UTC
    return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  },
      initDatePickers() {
      const commonOptions = {
        enableTime: true,
        dateFormat: 'Y-m-d H:i:S', // exactly like your old file
        time_24hr: true,
        allowInput: true,
      };

      // Just to sanity-check refs, optional:
      // console.log('refs at init:', this.$refs);

      if (this.$refs.discountStartsAtInput) {
        this.discountStartPicker = flatpickr(
          this.$refs.discountStartsAtInput,
          {
            ...commonOptions,
            defaultDate: this.discountStartsAt || null,
            onChange: (selectedDates, dateStr) => {
              this.discountStartsAt = dateStr;
            },
          }
        );
      }

      if (this.$refs.discountEndsAtInput) {
        this.discountEndPicker = flatpickr(
          this.$refs.discountEndsAtInput,
          {
            ...commonOptions,
            defaultDate: this.discountEndsAt || null,
            onChange: (selectedDates, dateStr) => {
              this.discountEndsAt = dateStr;
            },
          }
        );
      }
    },
    destroyDatePickers() {
      if (this.discountStartPicker) {
        this.discountStartPicker.destroy();
        this.discountStartPicker = null;
      }
      if (this.discountEndPicker) {
        this.discountEndPicker.destroy();
        this.discountEndPicker = null;
      }
    },

   // Your existing fetch method, with a small addition at the end
   async fetchAnalysisPricing() {
  this.pricingLoading = true;
  try {
    const resp = await axios.get(
      'etherscan/analysis_pricing_settings.php',
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    if (resp.data && resp.data.status === 'success') {
      this.creditsPerAnalysis     = resp.data.creditsPerAnalysis;
      this.creditsPerNormalMode   = resp.data.creditsPerNormalMode   ?? resp.data.creditsPerAnalysis;
      this.creditsPerAdvancedMode = resp.data.creditsPerAdvancedMode ?? resp.data.creditsPerAnalysis;
      this.creditsPerLinkcheck    = resp.data.creditsPerLinkcheck    ?? 1;

      this.discountActive   = !!resp.data.discountActive;
      this.discountAmount   = resp.data.discountAmount || 0;
      this.discountStartsAt = resp.data.discountStartsAt || '';
      this.discountEndsAt   = resp.data.discountEndsAt || '';
      this.pricingUpdatedAt = resp.data.updatedAt || null;
      this.pricingUpdatedBy = resp.data.updatedBy || null;
      this.pricingUpdatedIp = resp.data.updatedIp || null;

      this.$nextTick(() => {
        if (this.discountStartPicker) {
          this.discountStartPicker.setDate(
            this.discountStartsAt || null,
            false,
            'Y-m-d H:i:S'
          );
        }
        if (this.discountEndPicker) {
          this.discountEndPicker.setDate(
            this.discountEndsAt || null,
            false,
            'Y-m-d H:i:S'
          );
        }
      });
    } else {
      this.statusMessage =
        (resp.data && resp.data.message) ||
        'Failed to load analysis pricing settings.';
      this.statusType = 'error';
    }
  } catch (err) {
    console.error('fetchAnalysisPricing error:', err);
    this.statusMessage = 'Failed to load analysis pricing settings.';
    this.statusType = 'error';
  } finally {
    this.pricingLoading = false;
  }
},
async saveAnalysisPricing() {
  if (this.pricingSaving) return;

  if (!this.creditsPerAnalysis || this.creditsPerAnalysis <= 0) {
    this.statusMessage = 'Credits per analysis must be a positive integer.';
    this.statusType = 'error';
    return;
  }

  if (!this.creditsPerNormalMode || this.creditsPerNormalMode <= 0) {
    this.statusMessage = 'Normal mode credits must be a positive integer.';
    this.statusType = 'error';
    return;
  }

  if (!this.creditsPerAdvancedMode || this.creditsPerAdvancedMode <= 0) {
    this.statusMessage = 'Advanced mode credits must be a positive integer.';
    this.statusType = 'error';
    return;
  }

  if (!this.creditsPerLinkcheck || this.creditsPerLinkcheck <= 0) {
    this.statusMessage = 'LinkCheck credits must be a positive integer.';
    this.statusType = 'error';
    return;
  }

  if (this.discountAmount < 0) {
    this.statusMessage = 'Discount amount cannot be negative.';
    this.statusType = 'error';
    return;
  }
  if (this.discountAmount > this.creditsPerAnalysis) {
    this.statusMessage =
      'Discount amount cannot exceed credits per analysis.';
    this.statusType = 'error';
    return;
  }

  this.pricingSaving = true;
  try {
    const payload = {
      creditsPerAnalysis: this.creditsPerAnalysis,
      creditsPerNormalMode: this.creditsPerNormalMode,
      creditsPerAdvancedMode: this.creditsPerAdvancedMode,
      creditsPerLinkcheck: this.creditsPerLinkcheck,
      discountActive: this.discountActive,
      discountAmount: this.discountActive ? this.discountAmount : 0,
      discountStartsAt:
        this.discountActive && this.discountAmount > 0
          ? (this.discountStartsAt || '')
          : '',
      discountEndsAt:
        this.discountActive && this.discountAmount > 0
          ? (this.discountEndsAt || '')
          : '',
    };

    const resp = await axios.post(
      'etherscan/analysis_pricing_settings.php',
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    if (resp.data && resp.data.status === 'success') {
      this.statusMessage = 'Analysis pricing settings updated.';
      this.statusType = 'success';
      this.fetchAnalysisPricing();
    } else {
      this.statusMessage =
        (resp.data && resp.data.message) ||
        'Failed to update analysis pricing settings.';
      this.statusType = 'error';
    }
  } catch (err) {
    console.error('saveAnalysisPricing error:', err);
    this.statusMessage = 'Failed to update analysis pricing settings.';
    this.statusType = 'error';
  } finally {
    this.pricingSaving = false;
  }
},


     async fetchUploadPauseState() {
      try {
      const resp = await axios.get(
      'etherscan/upload_pause_flag.php',
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );
        
        if (resp.data && resp.data.status === 'success') {
          this.uploadPaused = !!resp.data.uploadPaused;
        } else {
          this.statusMessage =
            (resp.data && resp.data.message) ||
            'Failed to fetch upload status.';
          this.statusType = 'error';
        }
      } catch (err) {
        console.error('fetchUploadPauseState error:', err);
        this.statusMessage = 'Failed to fetch upload status.';
        this.statusType = 'error';
      }
    },

   async toggleUploadPause() {
  if (this.isTogglingUploadPause) return;

  this.isTogglingUploadPause = true;
  try {
    const resp = await axios.post(
      'etherscan/upload_pause_flag.php',
      {}, // no body needed
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );

    if (resp.data && resp.data.status === 'success') {
      this.uploadPaused = !!resp.data.uploadPaused;
      this.statusMessage = this.uploadPaused
        ? 'Result uploads are now PAUSED.'
        : 'Result uploads have been RESUMED.';
      this.statusType = 'success';
    } else {
      this.statusMessage =
        (resp.data && resp.data.message) ||
        'Failed to toggle upload status.';
      this.statusType = 'error';
    }
  } catch (err) {
    console.error('toggleUploadPause error:', err);
    this.statusMessage = 'Failed to toggle upload status.';
    this.statusType = 'error';
  } finally {
    this.isTogglingUploadPause = false;
  }
}
,
    copy(text) {
      navigator.clipboard?.writeText(text).catch(() => {});
    },

    // ==== Logs ====
    fetchLogs() {
      axios.get('etherscan/fetch_to_address_logs.php', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { page: this.logPage }
      }).then(res => {
        if (res.data.status === 'success') {
          this.logs = res.data.logs;
          this.totalLogs = res.data.total;
          this.statusMessage = '';
        } else {
          this.statusMessage = res.data.message;
          this.statusType = 'error';
        }
      }).catch(() => {
        this.statusMessage = 'Failed to fetch logs.';
        this.statusType = 'error';
      });
    },
    nextPage() { this.logPage++; this.fetchLogs(); },
    prevPage() { if (this.logPage > 1) { this.logPage--; this.fetchLogs(); } },

    // ==== Invites ====
    sendSimpleInvite() {
      axios.post('etherscan/send_simple_invite.php',
        { email: this.simpleInviteEmail },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      )
      .then(response => {
        if (response.data.status === 'success') {
          this.statusMessage = 'Simple invite sent successfully.';
          this.statusType = 'success';
          this.simpleInviteEmail = '';
          this.fetchInvites?.();
        } else {
          this.statusMessage = 'Failed to send invite: ' + response.data.message;
          this.statusType = 'error';
        }
      })
      .catch(() => {
        this.statusMessage = 'An error occurred while sending the invite.';
        this.statusType = 'error';
      });
    },
    sendInvite() {
      if (!this.isValidUsername(this.inviteUsername)) {
        this.statusMessage = 'Invalid username. Must start with a letter and use only letters, numbers, or underscores.';
        this.statusType = 'error';
        return;
      }
      axios.post('etherscan/send_invite.php',
        { username: this.inviteUsername, email: this.email },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      )
      .then(response => {
        if (response.data.status === 'success') {
          this.statusMessage = 'Invite sent successfully.';
          this.statusType = 'success';
          this.inviteUsername = '';
          this.email = '';
          this.fetchInvites();
        } else {
          this.statusMessage = 'Failed to send invite: ' + response.data.message;
          this.statusType = 'error';
        }
      })
      .catch(() => {
        this.statusMessage = 'An error occurred while sending the invite.';
        this.statusType = 'error';
      });
    },
    isValidUsername(name) {
      return /^[a-zA-Z][a-zA-Z0-9_]{2,49}$/.test(name);
    },
    fetchInvites() {
      axios.get('etherscan/get_pending_invites.php', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(res => {
        if (res.data.status === 'success') {
          this.pendingInvites = res.data.invites;
        }
      });
    },
    resendInvite(invite) {
      axios.post('etherscan/send_invite.php',
        { username: invite.username, email: invite.email },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      ).then(res => {
        this.statusMessage = res.data.status === 'success' ? 'Invite resent.' : res.data.message;
        this.statusType = 'success';
      });
    },
    revokeInvite(id) {
      axios.post('etherscan/revoke_invite.php',
        { id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      ).then(res => {
        this.statusMessage = res.data.status === 'success' ? 'Invite revoked.' : res.data.message;
        this.statusType = 'success';
        this.fetchInvites();
      });
    },

    // ==== Users ====
    normalizeAdminBoolean(value) {
      if (value === true || value === 1) return true;
      if (value === false || value === 0 || value == null) return false;

      const normalized = String(value).trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    },

    isCaseBuddyStaff(user) {
      const role = String(user?.role || '').trim().toLowerCase();
      return role === 'admin' || role === 'moderator';
    },

    caseBuddyRestrictionActive(user) {
      const value = user?.case_buddy_is_restricted_active ?? user?.is_restricted;
      return this.normalizeAdminBoolean(value);
    },

    caseBuddyHasAccessSource(user) {
      return this.isCaseBuddyStaff(user) || this.normalizeAdminBoolean(user?.can_use_case_buddy);
    },

    caseBuddyAccessLabel(user) {
      const restricted = this.caseBuddyRestrictionActive(user);

      if (this.isCaseBuddyStaff(user)) {
        return restricted ? 'Staff access · blocked' : 'Staff access';
      }

      if (!user?.case_buddy_permission_loaded) {
        return 'Unavailable';
      }

      if (this.normalizeAdminBoolean(user?.can_use_case_buddy)) {
        return restricted ? 'Enabled · blocked' : 'Enabled';
      }

      return 'Disabled';
    },

    caseBuddyAccessPill(user) {
      if (this.caseBuddyRestrictionActive(user) && this.caseBuddyHasAccessSource(user)) {
        return 'pill--danger';
      }

      if (this.isCaseBuddyStaff(user)) {
        return 'pill--ok';
      }

      if (!user?.case_buddy_permission_loaded) {
        return 'pill--queued';
      }

      return this.normalizeAdminBoolean(user?.can_use_case_buddy)
        ? 'pill--ok'
        : 'pill--queued';
    },

    canManageCaseBuddyPermission(user) {
      if (this.isCaseBuddyStaff(user)) return false;
      if (!user?.case_buddy_permission_loaded) return true;
      return this.normalizeAdminBoolean(user?.case_buddy_permission_editable);
    },

    isCaseBuddyPermissionSaving(userId) {
      return this.normalizeAdminBoolean(
        this.caseBuddyPermissionSaving[String(userId)]
      );
    },

    caseBuddyPermissionButtonDisabled(user) {
      return !user?.case_buddy_permission_loaded ||
        this.isCaseBuddyPermissionSaving(user?.id);
    },

    async fetchCaseBuddyPermissions() {
      const ids = this.users
        .map(user => Number(user?.id || 0))
        .filter(id => Number.isInteger(id) && id > 0);

      if (!ids.length) return;

      try {
        const response = await axios.get('etherscan/casebuddy_user_access.php', {
          params: { ids: ids.join(',') },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        if (response?.data?.status !== 'success') {
          throw new Error(response?.data?.message || 'Case Buddy access could not be loaded.');
        }

        const permissionById = new Map(
          (response.data.permissions || []).map(permission => [
            Number(permission.id),
            permission
          ])
        );

        this.users = this.users.map(user => {
          const permission = permissionById.get(Number(user.id));
          if (!permission) {
            return {
              ...user,
              case_buddy_permission_loaded: false
            };
          }

          return {
            ...user,
            can_use_case_buddy: this.normalizeAdminBoolean(
              permission.can_use_case_buddy
            ),
            case_buddy_effective_access: this.normalizeAdminBoolean(
              permission.effective_access
            ),
            case_buddy_access_source: permission.access_source || 'none',
            case_buddy_is_restricted_active: this.normalizeAdminBoolean(
              permission.is_restricted_active
            ),
            case_buddy_permission_editable: this.normalizeAdminBoolean(
              permission.permission_editable
            ),
            case_buddy_permission_loaded: true
          };
        });
      } catch (error) {
        this.users = this.users.map(user => ({
          ...user,
          case_buddy_permission_loaded: false
        }));

        this.statusMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Case Buddy access could not be loaded.';
        this.statusType = 'error';
      }
    },

    async setCaseBuddyAccess(user, enabled) {
      const userId = Number(user?.id || 0);
      if (!Number.isInteger(userId) || userId <= 0) {
        this.statusMessage = 'A valid user is required.';
        this.statusType = 'error';
        return;
      }

      const savingKey = String(userId);
      this.caseBuddyPermissionSaving = {
        ...this.caseBuddyPermissionSaving,
        [savingKey]: true
      };

      try {
        const response = await axios.post(
          'etherscan/casebuddy_user_access.php',
          { id: userId, enabled: Boolean(enabled) },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );

        if (response?.data?.status !== 'success' || !response?.data?.permission) {
          throw new Error(response?.data?.message || 'Case Buddy access could not be updated.');
        }

        const permission = response.data.permission;
        const userIndex = this.users.findIndex(
          candidate => Number(candidate?.id || 0) === userId
        );

        if (userIndex >= 0) {
          this.users.splice(userIndex, 1, {
            ...this.users[userIndex],
            can_use_case_buddy: this.normalizeAdminBoolean(
              permission.can_use_case_buddy
            ),
            case_buddy_effective_access: this.normalizeAdminBoolean(
              permission.effective_access
            ),
            case_buddy_access_source: permission.access_source || 'none',
            case_buddy_is_restricted_active: this.normalizeAdminBoolean(
              permission.is_restricted_active
            ),
            case_buddy_permission_editable: this.normalizeAdminBoolean(
              permission.permission_editable
            ),
            case_buddy_permission_loaded: true
          });
        }

        this.statusMessage = enabled
          ? `Case Buddy access enabled for ${permission.username}.`
          : `Case Buddy access disabled for ${permission.username}.`;
        this.statusType = 'success';
      } catch (error) {
        this.statusMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Case Buddy access could not be updated.';
        this.statusType = 'error';
      } finally {
        const nextSavingState = { ...this.caseBuddyPermissionSaving };
        delete nextSavingState[savingKey];
        this.caseBuddyPermissionSaving = nextSavingState;
      }
    },

    toggleNormalMode(userId) {
      axios.post('etherscan/toggle_normal_mode.php',
        { id: userId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      ).then(response => {
        if (response.data.status === 'success') {
          this.fetchUsers();
          this.statusMessage = 'Advanced mode permission updated successfully.';
          this.statusType = 'success';
        } else {
          this.statusMessage = 'Failed to update permission: ' + response.data.message;
          this.statusType = 'error';
        }
      }).catch(() => {
        this.statusMessage = 'An error occurred while updating permission.';
        this.statusType = 'error';
      });
    },
    createUser() {
      if (!this.isValidUsername(this.username)) {
        this.statusMessage = 'Invalid username. Must start with a letter and use only letters, numbers, or underscores.';
        this.statusType = 'error';
        return;
      }
      axios.post('etherscan/create_user.php',
        { username: this.username, password: this.password },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      ).then(response => {
        if (response.data.status === 'success') {
          this.fetchUsers();
          this.username = '';
          this.password = '';
          this.statusMessage = 'User created successfully.';
          this.statusType = 'success';
        } else {
          this.statusMessage = 'Failed to create user: ' + response.data.message;
          this.statusType = 'error';
        }
      }).catch(() => {
        this.statusMessage = 'An error occurred while creating the user.';
        this.statusType = 'error';
      });
    },
    fetchUsers() {
      this.isLoading = true
      axios.get('etherscan/get_users.php', {
        params: { page: this.page, limit: this.limit },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => {
        if (res.data.status === 'success') {
          this.users = (res.data.users || []).map(user => ({
            ...user,
            can_use_case_buddy: false,
            case_buddy_permission_loaded: false
          }))
          this.total = Number(res.data.total || 0)
          // if API returns page/limit, prefer those to stay in sync:
          if (res.data.page)  this.page  = Number(res.data.page)
          if (res.data.limit) this.limit = Number(res.data.limit)
          return this.fetchCaseBuddyPermissions()
        }

        this.statusMessage = 'Failed to fetch users: ' + (res.data.message || '')
        this.statusType = 'error'
        return null
      })
      .catch(error => {
        this.statusMessage =
          error?.response?.data?.message ||
          'An error occurred while fetching users.'
        this.statusType = 'error'
      })
      .finally(() => { this.isLoading = false })
    },

    nextPageUsers() {
      if (this.page < this.totalPages) { this.page++; this.fetchUsers() }
    },
    prevPageUsers() {
      if (this.page > 1) { this.page--; this.fetchUsers() }
    },
    setPage(p) {
      const target = Math.min(this.totalPages, Math.max(1, p))
      if (target !== this.page) { this.page = target; this.fetchUsers() }
    },
    changeLimit(newLim) {
      this.limit = Number(newLim) || 25
      this.page = 1
      this.fetchUsers()
    },
    confirmDeleteUser(userId, username) {
      this.userToDelete = { id: userId, username };
      this.showDeleteConfirmation = true;
    },
    deleteUser() {
      axios.post('etherscan/delete_user.php',
        { id: this.userToDelete.id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      ).then(response => {
        if (response.data.status === 'success') {
          this.fetchUsers();
          this.statusMessage = 'User deleted successfully.';
          this.statusType = 'success';
        } else {
          this.statusMessage = 'Failed to delete user: ' + response.data.message;
          this.statusType = 'error';
        }
      }).catch(() => {
        this.statusMessage = 'An error occurred while deleting the user.';
        this.statusType = 'error';
      }).finally(() => {
        this.cancelDelete();
      });
    },
    cancelDelete() {
      this.showDeleteConfirmation = false;
      this.userToDelete = { id: null, username: '' };
    },
    changeRole(userId) {
      axios.post('etherscan/change_role.php',
        { id: userId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      ).then(response => {
        if (response.data.status === 'success') {
          this.fetchUsers();
          this.statusMessage = 'User role changed successfully.';
          this.statusType = 'success';
        } else {
          this.statusMessage = 'Failed to change user role: ' + response.data.message;
          this.statusType = 'error';
        }
      }).catch(() => {
        this.statusMessage = 'An error occurred while changing the user role.';
        this.statusType = 'error';
      });
    },

    rolePill(role) {
      return role === 'admin' ? 'pill--admin' :
             role === 'moderator' ? 'pill--mod' : 'pill--ok';
    }
  }
};
</script>

<style scoped>
/* Page */
.admin-page {
  max-width: 1120px;
  margin: 0 auto;
  padding: 1rem;
  color: #1f2a37;
}
.page-head h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f2740;
}
.sub { color: #667085; margin: .15rem 0 .8rem; }

/* Toast */
.toast {
  padding: .6rem .8rem;
  border-radius: 10px;
  margin: .2rem 0 1rem;
  font-weight: 600;
  border: 1px solid transparent;
}
.toast.success { background:#ecfdf5; color:#065f46; border-color:#a7f3d0; }
.toast.error   { background:#fef2f2; color:#991b1b; border-color:#fecaca; }

/* Cards */
.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 10px 20px rgba(16,24,40,.06);
  padding: 1rem;
  margin-bottom: 1rem;
}
.card__head { margin-bottom: .6rem; }
.card__head h2, .card__head h3 { margin: 0; }
.card__head.-tight { margin-top: .2rem; }
.muted { color: #6b7280; }
.permission-note {
  display: block;
  margin-top: .3rem;
  max-width: 13rem;
  font-size: .74rem;
  line-height: 1.25;
}
.divider {
  height: 1px; background: #eef1f5; margin: 1rem 0;
}

/* Forms */
.form-grid {
  display: grid;
  gap: .8rem;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
}
.form-inline {
  display: flex;
  gap: .6rem;
  align-items: center;
  flex-wrap: wrap;
}
.grow { flex: 1 1 320px; }
.field { display: grid; gap: .35rem; }
.field > span { font-size: .9rem; color: #475467; }
input, select {
  padding: .6rem .7rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  width: 100%;
}
.actions { grid-column: 1 / -1; display: flex; gap: .6rem; }

/* Buttons */
.btn {
  padding: .6rem .8rem;
  border-radius: 10px;
  border: 1px solid rgba(15,38,61,.15);
  background: linear-gradient(180deg,#ffffff,#f3f4f6);
  cursor: pointer;
  font-weight: 600;
}
.btn:hover { background: linear-gradient(180deg,#f9fafb,#eef2f7); }
.btn:disabled { opacity:.6; cursor: not-allowed; }
.btn--ghost { background:#f9fafb; border:1px solid #e5e7eb; }
.btn--danger { background:#fee2e2; border:1px solid #fecaca; color:#991b1b; }
.small { padding:.4rem .6rem; font-size:.9rem; }

.icon-btn {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  padding: .1rem .35rem;
  margin-left: .35rem;
  cursor: pointer;
}
.icon-btn:hover { background: #f3f4f6; }

/* Tables */
.table-wrap {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}
.nice-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 760px;
  table-layout: fixed;
  background: #fff;
}
.nice-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f3f6fb;
  color: #0f2740;
  text-align: left;
  font-weight: 700;
  font-size: .92rem;
  border-bottom: 1px solid #e5e7eb;
  padding: .7rem .7rem;
}
.nice-table td {
  padding: .6rem .7rem;
  border-bottom: 1px solid #eef1f5;
  vertical-align: top;
}
.row-actions { display: flex; gap: .4rem; flex-wrap: wrap; }

.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }
.semi { font-weight: 600; }
.truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Pills */
.pill {
  display: inline-block;
  padding: .15rem .5rem;
  border-radius: 999px;
  font-size: .78rem;
  font-weight: 700;
  border: 1px solid transparent;
}
.pill--ok { background:#e8faf3; color:#065f46; border-color:#a7f3d0; }
.pill--queued { background:#eef2ff; color:#4338ca; border-color:#c7d2fe; }
.pill--admin { background:#fde8e8; color:#991b1b; border-color:#fecaca; }
.pill--mod { background:#ecfeff; color:#155e75; border-color:#a5f3fc; }

/* Pagination */
.pagination {
  display: flex; gap: .6rem; align-items: center; justify-content: center;
  margin-top: .75rem;
}

/* Quick nav */
.quick-nav {
  display: flex; flex-wrap: wrap; gap: .6rem; justify-content: center;
  margin: 1rem 0;
}
.quick-nav a {
  padding: .45rem .7rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}

/* Modal */
.modal {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.45);
  display: grid; place-items: center;
  z-index: 1000;
}
.modal__panel {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 50px rgba(0,0,0,.25);
  padding: 1rem;
  width: min(480px, 92vw);
}
.modal__panel h3 { margin: 0 0 .35rem; }
.modal__actions { display: flex; gap: .5rem; justify-content: flex-end; margin-top: .6rem; }

/* Responsive */
@media (max-width: 860px) {
  .form-grid { grid-template-columns: 1fr; }
  .nice-table { min-width: 640px; }
}

/* Mobile cards for lists */
.cards { display: none; }
@media (max-width: 720px) {
  .table-wrap { display: none; }
  .cards { display: grid; gap: .8rem; }
  .mcard {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 6px 14px rgba(16,24,40,.06);
    padding: .9rem;
  }
  .mcard__head {
    display: flex; align-items: center; justify-content: space-between; gap: .6rem;
    margin-bottom: .35rem;
  }
  .mcard__title { font-weight: 700; }
  .kv { display: grid; gap: .35rem; }
  .kv > div { display: grid; grid-template-columns: 110px 1fr; gap: .4rem; align-items: baseline; }
  .kv dt { color: #6b7280; font-size: .86rem; }
  .kv dd { margin: 0; }
  .mcard__actions { margin-top: .6rem; display: flex; flex-wrap: wrap; gap: .4rem; }
}

/* a11y */
.sr-only {
  position: absolute !important;
  height: 1px; width: 1px; overflow: hidden;
  clip: rect(1px,1px,1px,1px); white-space: nowrap;
}

.pagination-row { display:flex; justify-content:space-between; align-items:center; padding:12px; }
.pagination-row .pager { display:flex; gap:10px; align-items:center; }
.pagination-row .muted { opacity:.8; }


.pill {
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
}

.pill--success {
  background: #e3f8e6;
  color: #176b38;
}

.pill--danger {
  background: #fde4e4;
  color: #9d1b1b;
}

/* Make certain fields span the full width of the grid */
.form-grid .field--full {
  grid-column: 1 / -1;
}

/* Discount panel styling */
.pricing-discount-panel {
  margin-top: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  background: #f8fafc; /* light neutral background; adjust to match your theme */
  border: 1px solid rgba(15, 23, 42, 0.06); /* subtle border */
}

.pricing-discount-panel__head {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

/* Inline checkbox label */
.checkbox-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}


/* Optional: space the Save button a bit from the content */
.card .actions {
  margin-top: 1rem;
}
.pricing-discount-panel__grid {
  display: grid;
  gap: 0.75rem;
}

/* On wider screens, allow 2–3 columns depending on width */
@media (min-width: 720px) {
  .pricing-discount-panel__grid {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
}
.per-mode-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}
@media (max-width: 960px) {
  .per-mode-grid {
    grid-template-columns: 1fr;
  }
}
.per-mode-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

@media (max-width: 960px) {
  .per-mode-grid {
    grid-template-columns: 1fr;
  }
}

</style>
